import { IEntity } from '@/models/common/entity';
import { Action, Mutation, VuexModule } from 'vuex-class-modules';
import { HttpService } from '@/services/http-service';
import { freeze } from '@/shared/utils/freeze';
import { mergeOptions } from '@/shared/utils/merge-options';
import { RecordStatus } from '@/models/common/record-status';
import { IFieldDescription } from '@/models/common/field-description';

export interface IBaseModule<T> {
    records: T[];
    selectedItem: T;
    editingItem: T;
    validationErrors: any;
    isDeleting: boolean
    isEditingItemDirty: boolean;
    recordStatus: RecordStatus
    canSave: boolean
    canCancel: boolean
    canDelete: boolean
    canCopy: boolean
    canAdd: boolean
    canRefresh: boolean

    reload(): Promise<T[]>
    updateEditingItemField(payload: IFieldDescription)
    reset(payload?: IResetPayload)
    getRecords?(payload?: any): Promise<T[]>
    cancel()
    delete?(payload?: any): Promise<any>
    save?(): Promise<T>
    newRecord(payload?: any)
    copy()
    selectItem(item: T)
    totalRecordsCount(): Promise<number>
}

export interface IResetPayload {
    resetRecords: boolean
}

export abstract class BaseModule<T extends IEntity> extends VuexModule implements IBaseModule<T> {
    abstract apiService(): HttpService
    abstract cloneRecord(record: T): T
    abstract copyRecord(record: T): T
    abstract createRecord(params?: any): T

    records: T[] = [];
    totalCount: number = 0;
    validationErrors: any = null;
    selectedItem: T = null;
    editingItem: T = null;
    isEditingItemDirty: boolean = false;
    isDeleting: boolean = false

    get isEditingItemNew() { return this.editingItem && this.editingItem.isNew }

    get recordStatus() {
        if (!this.editingItem) { return RecordStatus.NORECORD; }
        if (this.isEditingItemNew) { return RecordStatus.NEW; }
        if (this.isEditingItemDirty) { return RecordStatus.CHANGED; }
        if (!this.isEditingItemDirty) { return RecordStatus.DISPLAY; }
        throw Error('Unable to determine current record status.');
    }

    @Action
    async reload() {
        this.reset()
        return await this._baseGetRecords();
    }

    @Action
    updateEditingItemField(payload: IFieldDescription) {
        if (this.editingItem[payload.field] !== payload.value) {
            this.editingItem = Object.assign(this.editingItem, { [payload.field]: payload.value })
            this.isEditingItemDirty = true
        }
    }

    @Action
    reset(payload?: IResetPayload) {
        const options = mergeOptions(payload, {
            resetRecords: true,
        })

        if (options.resetRecords) {
            this.setRecords([])
        }

        this.editingItem = null
        this.isEditingItemDirty = false
        this.validationErrors = null
    }

    get canSave() { return [RecordStatus.NEW, RecordStatus.CHANGED].includes(this.recordStatus) }
    get canCancel() { return [RecordStatus.NEW, RecordStatus.CHANGED].includes(this.recordStatus) }
    get canDelete() { return [RecordStatus.DISPLAY].includes(this.recordStatus) }
    get canCopy() { return [RecordStatus.DISPLAY].includes(this.recordStatus) }
    get canAdd() { return [RecordStatus.DISPLAY, RecordStatus.NORECORD].includes(this.recordStatus) }
    get canRefresh() { return [RecordStatus.DISPLAY, RecordStatus.NORECORD].includes(this.recordStatus) }

    @Mutation
    setRecords(records) {
        this.records = freeze(records)
    }

    @Mutation
    setTotalCount(count) {
        this.totalCount = freeze(count)
    }

    @Action
    protected async _baseGetRecords(params?: any) {
        this.setRecords(await this.apiService().getAll<T>({ params }))
        return this.records
    }

    @Action
    protected async _baseSave(payload?: any) {
        this.validationErrors = null;

        const data = payload === undefined
            ? this.editingItem
            : payload

        const saveRequest = data.isNew
            ? this.apiService().create<T>({ data })
            : this.apiService().update<T>({ resourceId: data.id, data })

        try {
            const record = await saveRequest

            this.setRecords(data.isNew
                ? this.records.concat(record)
                : this.records.map(p => (p.id === record.id ? record : p)));

            this.selectItem(record);

            return record;
        } catch (e) {
            this.validationErrors = e
            throw e
        }
    }

    @Action
    protected async _baseDelete(resetRecords: boolean = true) {
        this.validationErrors = null

        try {
            const recordId = this.editingItem.id

            await this.apiService().delete({ resourceId: recordId })

            if (resetRecords) {
                this.reset({ resetRecords })
            } else {
                this.setRecords(this.records.filter(p => p.id !== this.editingItem.id))
                this.selectItem(null)
            }
        } catch (error) {
            this.validationErrors = error
            throw error
        }
    }

    @Action
    newRecord(params: any) {
        this.validationErrors = null
        this.isEditingItemDirty = false
        this.editingItem = this.createRecord(params)
    }

    @Action
    copy() {
        this.editingItem = this.copyRecord(this.selectedItem)
        this.validationErrors = null
        this.isEditingItemDirty = true
    }

    @Action
    cancel() {
        this.selectItem(this.selectedItem)
    }

    @Action
    selectItem(item: T) {
        this.validationErrors = null
        this.isEditingItemDirty = false
        this.selectedItem = item
        this.editingItem = this.cloneRecord(item)
    }

    @Action
    async totalRecordsCount() {
        this.setTotalCount(await this.apiService().getTotalCount({path: '/get/TotalCount'}))
        return this.totalCount;
    }
}
